import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { MapPin, Building2, Truck, Package, Clock, Search, ChevronDown, ChevronUp, Car } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOFIA_CITY_VARIANTS = ['софия', 'sofia', 'sofia city', 'столична община', 'софия град', 'sofia grad'];

const isSofiaCity = (name) => {
  if (!name) return false;
  const normalized = name.toLowerCase().replace(/гр\.?/g, ' ').replace(/\s+/g, ' ').trim();
  return SOFIA_CITY_VARIANTS.includes(normalized);
};

const EcontDeliverySelector = ({ 
  onDeliveryChange, 
  cartTotal = 0,
  cartWeight = 0.5,
  defaultCity = '',
  defaultPostcode = ''
}) => {
  const [deliveryMethod, setDeliveryMethod] = useState('office'); // Default to office (cheaper)
  const [cities, setCities] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [searchCity, setSearchCity] = useState(defaultCity);
  const [selectedCity, setSelectedCity] = useState(null);
  const [postcode, setPostcode] = useState(defaultPostcode);
  const [streetAddress, setStreetAddress] = useState('');
  const [shippingCost, setShippingCost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOffices, setShowOffices] = useState(false);
  const [officeSearch, setOfficeSearch] = useState('');
  const [sofiaSettings, setSofiaSettings] = useState({ enabled: true, threshold: 25, fee_above_threshold: 0 });

  // Fetch Sofia delivery settings
  useEffect(() => {
    axios.get(`${API}/delivery/sofia-settings`)
      .then(res => setSofiaSettings(res.data))
      .catch(() => {});
  }, []);

  const isSofia = sofiaSettings.enabled && selectedCity && isSofiaCity(selectedCity.name);

  // Fetch cities on search
  useEffect(() => {
    const fetchCities = async () => {
      if (searchCity.length < 2) {
        setCities([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/econt/cities`, {
          params: { name: searchCity }
        });
        setCities(response.data.cities || []);
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    };

    const debounce = setTimeout(fetchCities, 300);
    return () => clearTimeout(debounce);
  }, [searchCity]);

  // Fetch offices when city is selected and method is 'office' (not for Sofia)
  useEffect(() => {
    const fetchOffices = async () => {
      if (!selectedCity || deliveryMethod !== 'office' || isSofia) {
        setOffices([]);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get(`${API}/econt/offices`, {
          params: { city: selectedCity.name }
        });
        setOffices(response.data.offices || []);
      } catch (error) {
        console.error('Error fetching offices:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOffices();
  }, [selectedCity, deliveryMethod, isSofia]);

  // Calculate shipping cost
  useEffect(() => {
    const calculateShipping = async () => {
      if (!selectedCity) {
        setShippingCost(null);
        onDeliveryChange?.(null);
        return;
      }

      // ===== SOFIA: own driver delivery, no Econt =====
      if (isSofia) {
        if (!streetAddress.trim()) {
          setShippingCost(null);
          onDeliveryChange?.(null);
          return;
        }
        const fee = cartTotal <= sofiaSettings.threshold ? 0 : Number(sofiaSettings.fee_above_threshold || 0);
        setShippingCost({
          totalPrice: fee,
          currency: 'EUR',
          sofiaDriver: true,
          freeShipping: fee === 0
        });
        onDeliveryChange?.({
          method: 'sofia_driver',
          city: selectedCity.name,
          postcode: selectedCity.postCode || postcode,
          office: null,
          streetAddress: streetAddress,
          shippingCost: fee,
          freeShipping: fee === 0,
          currency: 'EUR',
          deliveryDays: 1
        });
        return;
      }

      // ===== OUTSIDE SOFIA: Econt calculation =====
      if (deliveryMethod === 'address' && !streetAddress.trim()) {
        setShippingCost(null);
        onDeliveryChange?.(null);
        return;
      }
      
      if (deliveryMethod === 'office' && !selectedOffice) {
        setShippingCost(null);
        onDeliveryChange?.(null);
        return;
      }

      try {
        const response = await axios.post(`${API}/econt/calculate`, {
          city: selectedCity.name,
          postcode: selectedCity.postCode || postcode,
          weight: Math.max(cartWeight, 0.3),
          office_code: deliveryMethod === 'office' && selectedOffice ? selectedOffice.code : null,
          cod_amount: 0,
          declared_value: cartTotal
        });

        if (response.data.success) {
          setShippingCost({
            ...response.data,
            sofiaDriver: false,
            freeShipping: false
          });
          
          onDeliveryChange?.({
            method: deliveryMethod,
            city: selectedCity.name,
            postcode: selectedCity.postCode || postcode,
            office: selectedOffice,
            streetAddress: deliveryMethod === 'address' ? streetAddress : null,
            shippingCost: response.data.totalPrice,
            freeShipping: false,
            currency: response.data.currency,
            deliveryDays: response.data.deliveryDays,
            deliveryDeadline: response.data.deliveryDeadline,
            weight: response.data.weight
          });
        }
      } catch (error) {
        console.error('Error calculating shipping:', error);
      }
    };

    const debounce = setTimeout(calculateShipping, 500);
    return () => clearTimeout(debounce);
  }, [selectedCity, postcode, deliveryMethod, selectedOffice, streetAddress, cartTotal, cartWeight, onDeliveryChange, isSofia, sofiaSettings]);

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setSearchCity(city.name);
    setPostcode(city.postCode);
    setCities([]);
    setSelectedOffice(null);
    setShippingCost(null);
  };

  const handleOfficeSelect = (office) => {
    setSelectedOffice(office);
    setShowOffices(false);
  };

  const filteredOffices = offices.filter(o => 
    officeSearch === '' || 
    o.name?.toLowerCase().includes(officeSearch.toLowerCase()) ||
    o.address?.fullAddress?.toLowerCase().includes(officeSearch.toLowerCase())
  );

  return (
    <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800" data-testid="econt-delivery-selector">
      <div className="flex items-center gap-2 mb-4">
        {isSofia ? (
          <>
            <Car className="h-5 w-5 text-green-600" />
            <span className="font-semibold text-green-700 dark:text-green-300">Доставка в София с наш шофьор</span>
          </>
        ) : (
          <>
            <img 
              src="https://www.econt.com/static/media/Logo.8e8b5e88.svg" 
              alt="Econt" 
              className="h-6"
              onError={(e) => e.target.style.display = 'none'}
            />
            <span className="font-semibold text-amber-800 dark:text-amber-200">Доставка с Еконт</span>
          </>
        )}
      </div>

      {/* Delivery Method Selection - hidden for Sofia (own driver, address only) */}
      {!isSofia && (
        <div>
          <Label className="mb-2 block">Начин на получаване</Label>
          <RadioGroup 
            value={deliveryMethod} 
            onValueChange={(v) => {
              setDeliveryMethod(v);
              setSelectedOffice(null);
            }}
            className="grid grid-cols-2 gap-3"
          >
            <label 
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                deliveryMethod === 'address' 
                  ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/30' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
              }`}
              data-testid="delivery-to-address"
            >
              <RadioGroupItem value="address" className="sr-only" />
              <Truck className="w-5 h-5 text-amber-600" />
              <div>
                <div className="font-medium">До адрес</div>
                <div className="text-xs text-gray-500">Куриер до врата</div>
              </div>
            </label>
            
            <label 
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all relative ${
                deliveryMethod === 'office' 
                  ? 'border-amber-500 bg-amber-100 dark:bg-amber-900/30' 
                  : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
              }`}
              data-testid="delivery-to-office"
            >
              <RadioGroupItem value="office" className="sr-only" />
              <Building2 className="w-5 h-5 text-amber-600" />
              <div>
                <div className="font-medium flex items-center gap-2">
                  До офис
                  <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-normal">
                    Препоръчано
                  </span>
                </div>
                <div className="text-xs text-green-600 font-medium">По-евтино & бързо</div>
              </div>
            </label>
          </RadioGroup>
        </div>
      )}

      {/* City Selection */}
      <div className="relative">
        <Label htmlFor="city-search">Град</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="city-search"
            value={searchCity}
            onChange={(e) => {
              setSearchCity(e.target.value);
              setSelectedCity(null);
            }}
            placeholder="Въведете град..."
            className="pl-10"
            data-testid="city-search-input"
          />
        </div>
        
        {/* City Dropdown */}
        {cities.length > 0 && !selectedCity && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border rounded-lg shadow-lg max-h-60 overflow-auto">
            {cities.map((city) => (
              <button
                key={city.id}
                type="button"
                onClick={() => handleCitySelect(city)}
                className="w-full px-4 py-2 text-left hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2"
                data-testid={`city-option-${city.id}`}
              >
                <MapPin className="w-4 h-4 text-amber-500" />
                <div>
                  <span className="font-medium">{city.name}</span>
                  <span className="text-sm text-gray-500 ml-2">{city.postCode}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Sofia own-driver info banner */}
      {isSofia && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border-2 border-green-300 dark:border-green-700" data-testid="sofia-driver-banner">
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-800 dark:text-green-200">
                Доставка в гр. София с наш шофьор
                {(cartTotal <= sofiaSettings.threshold || Number(sofiaSettings.fee_above_threshold || 0) === 0) ? ' — Безплатна' : ` — ${Number(sofiaSettings.fee_above_threshold).toFixed(2)} €`}
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                Поръчките в София се доставят лично от наш шофьор, без Еконт.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Street Address (Sofia always requires address; otherwise when delivery to address) */}
      {selectedCity && (isSofia || deliveryMethod === 'address') && (
        <div>
          <Label htmlFor="street-address">Адрес за доставка *</Label>
          <Input
            id="street-address"
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
            placeholder="ул. Примерна №1, ет.2, ап.3"
            className="mt-1"
            data-testid="street-address-input"
          />
          <p className="text-xs text-gray-500 mt-1">Въведете пълен адрес с улица, номер, етаж и апартамент</p>
        </div>
      )}

      {/* Office Selection (when delivery to office, not Sofia) */}
      {!isSofia && deliveryMethod === 'office' && selectedCity && (
        <div>
          <Label>Изберете офис на Еконт</Label>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            </div>
          ) : (
            <div className="mt-2">
              {selectedOffice ? (
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-amber-300">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold text-amber-700 dark:text-amber-300">{selectedOffice.name}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{selectedOffice.address?.fullAddress}</div>
                      {selectedOffice.workingTime && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {selectedOffice.workingTime}
                        </div>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowOffices(!showOffices)}
                    >
                      Промени
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => setShowOffices(!showOffices)}
                  data-testid="select-office-btn"
                >
                  <span>Изберете офис</span>
                  {showOffices ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              )}

              {showOffices && (
                <div className="mt-2 border rounded-lg max-h-64 overflow-auto bg-white dark:bg-gray-800">
                  <div className="sticky top-0 p-2 bg-white dark:bg-gray-800 border-b">
                    <Input
                      value={officeSearch}
                      onChange={(e) => setOfficeSearch(e.target.value)}
                      placeholder="Търсене на офис..."
                      className="text-sm"
                    />
                  </div>
                  {filteredOffices.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      Няма намерени офиси в {selectedCity?.name}
                    </div>
                  ) : (
                    filteredOffices.map((office) => (
                      <button
                        key={office.code}
                        type="button"
                        onClick={() => handleOfficeSelect(office)}
                        className={`w-full p-3 text-left border-b last:border-b-0 hover:bg-amber-50 dark:hover:bg-amber-900/20 ${
                          selectedOffice?.code === office.code ? 'bg-amber-100 dark:bg-amber-900/30' : ''
                        }`}
                        data-testid={`office-option-${office.code}`}
                      >
                        <div className="font-medium text-sm">{office.name}</div>
                        <div className="text-xs text-gray-500">{office.address?.fullAddress}</div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          {office.workingTime && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {office.workingTime}
                            </span>
                          )}
                          {office.isAPS && (
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">
                              Автомат
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Shipping Cost Display */}
      {shippingCost && (
        <div className={`mt-4 p-3 rounded-lg border ${shippingCost.freeShipping 
          ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700' 
          : 'bg-white dark:bg-gray-800 border-amber-200 dark:border-amber-800'}`} data-testid="shipping-cost-display">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Package className={`w-5 h-5 ${shippingCost.freeShipping ? 'text-green-600' : 'text-amber-600'}`} />
              <span className="font-medium">Цена за доставка:</span>
            </div>
            <div className="text-right">
              {shippingCost.sofiaDriver ? (
                <div>
                  <div className="text-lg font-bold text-green-600">
                    {shippingCost.freeShipping ? 'Безплатна' : `${shippingCost.totalPrice.toFixed(2)} €`}
                  </div>
                  <div className="text-xs text-gray-500">
                    Доставка в гр. София с наш шофьор
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-lg font-bold text-amber-600">
                    {shippingCost.totalPrice.toFixed(2)} {shippingCost.currency}
                  </div>
                  <div className="text-xs text-gray-500">
                    Доставка за {shippingCost.deliveryDays} {shippingCost.deliveryDays === 1 ? 'работен ден' : 'работни дни'}
                  </div>
                  {shippingCost.deliveryDeadline && (
                    <div className="text-xs text-green-600 font-medium">
                      Очаквана доставка: {new Date(shippingCost.deliveryDeadline).toLocaleDateString('bg-BG', { day: 'numeric', month: 'long' })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EcontDeliverySelector;
